import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useGlobalContext } from '../GlobalContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Home = () => {
  const { periode, api, isReady } = useGlobalContext();

  const [usp, setUsp] = useState(0);
  const [manasuka, setManasuka] = useState(0);
  const [hariraya, setHariraya] = useState(0);
  const [kosong, setKosong] = useState(0);
  const [piutangData, setPiutangData] = useState([]);
  const [tagihanData, setTagihanData] = useState([]);
  const [tunggakanData, setTunggakanData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [uspRes, manasukaRes, harirayaRes] = await Promise.all([
          api.get('/api/anggota/totalusp'),
          api.get('/api/anggota/totalmanasuka'),
          api.get('/api/anggota/totalhariraya'),
        ]);

        setUsp(uspRes.data.Jumlah);
        setManasuka(manasukaRes.data.Jumlah);
        setHariraya(harirayaRes.data.Jumlah);
        setKosong(0);

        const piutangRes = await api.get(`/api/grafik/rekpinjamanpiutang/${periode}`);
        const piutang = piutangRes.data || [];
        piutang.sort((a, b) => a.periode.localeCompare(b.periode));
        setPiutangData(piutang);

        const potonganRes = await api.get(`/api/grafik/potongan/${periode}`);
        const potongan = potonganRes.data.data || [];
        potongan.sort((a, b) => a.periode.localeCompare(b.periode));
        setTagihanData(potongan);

        const tunggakanRes = await api.get(`/api/grafik/tunggakan/${periode}`);
        const tunggakan = tunggakanRes.data.data || [];
        tunggakan.sort((a, b) => a.periode.localeCompare(b.periode));
        setTunggakanData(tunggakan);

      } catch (error) {
        console.error('❌ Gagal mengambil data dashboard:', error);
      }
    };

    if (isReady && periode) {
      fetchData();
    }
  }, [isReady, periode, api]);

  const labels = piutangData.map(item => `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`);
  const tagihanLabels = tagihanData.map(item => `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`);
  const tunggakanLabels = tunggakanData.map(item => `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`);

  const totalPiutang = piutangData.map(item => parseFloat((item.totalpiutang / 1_000_000).toFixed(1)));
  const angsuran = piutangData.map(item => parseFloat((item.angsuran / 1_000_000).toFixed(1)));
  const tagihanValues = tagihanData.map(item => parseFloat((item.tagihan_a / 1_000_000).toFixed(1)));
  const tunggakanValues = tunggakanData.map(item => parseFloat((item.tunggakan / 1_000_000).toFixed(1)));

  const barChartData1 = {
    labels,
    datasets: [{
      label: 'Total Piutang (Juta)',
      backgroundColor: 'rgba(60, 141, 188, 0.9)',
      borderColor: 'rgba(60,141,188,0.8)',
      data: totalPiutang,
    }],
  };

  const barChartData2 = {
    labels,
    datasets: [{
      label: 'Angsuran (Juta)',
      backgroundColor: 'rgba(0,166,90,0.9)',
      borderColor: 'rgba(0,166,90,0.8)',
      data: angsuran,
    }],
  };

  const barChartData3 = {
    labels: tagihanLabels,
    datasets: [{
      label: 'Tagihan (Juta)',
      backgroundColor: 'rgba(243, 156, 18, 0.9)',
      borderColor: 'rgba(243, 156, 18, 0.8)',
      data: tagihanValues,
    }],
  };

  const barChartData4 = {
    labels: tunggakanLabels,
    datasets: [{
      label: 'Tunggakan (Juta)',
      backgroundColor: 'rgba(255, 99, 132, 0.9)',
      borderColor: 'rgba(255, 99, 132, 0.8)',
      data: tunggakanValues,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { stacked: false, ticks: { autoSkip: false } },
      y: { stacked: false, beginAtZero: true },
    },
  };

  return (
    <div className="wrapper">
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            {[{ bg: 'bg-info', count: usp, label: 'Anggota USP' },
              { bg: 'bg-success', count: manasuka, label: 'Anggota Manasuka' },
              { bg: 'bg-warning', count: hariraya, label: 'Anggota Hariraya' },
              { bg: 'bg-danger', count: kosong, label: 'Kosong' },
            ].map((item, index) => (
              <div key={index} className="col-lg-3 col-6">
                <div className={`small-box ${item.bg}`}>
                  <div className="inner">
                    <h3>{item.count}</h3>
                    <p>{item.label}</p>
                  </div>
                  <div className="icon">
                    <i className="ion ion-stats-bars" />
                  </div>
                  <a href="#" className="small-box-footer">More info <i className="fas fa-arrow-circle-right" /></a>
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="card card-primary">
                <div className="card-header">
                  <h3 className="card-title">Grafik Piutang</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={barChartData1} options={options} height={300} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card card-success">
                <div className="card-header">
                  <h3 className="card-title">Grafik Angsuran</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={barChartData2} options={options} height={300} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card card-warning">
                <div className="card-header">
                  <h3 className="card-title">Grafik Tagihan</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={barChartData3} options={options} height={300} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card card-danger">
                <div className="card-header">
                  <h3 className="card-title">Grafik Tunggakan</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={barChartData4} options={options} height={300} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;